import { isAdserviceDVTargetingEnabled, isArticleDecoupled, isPersonalizedTargetingEnabled } from "../util/pageEnabledChecker";
import UserInfo from "../session/UserInfo";
import { AdContextData, ExcludeTargetingType, TARGETING_TYPES } from "../type";
import Ad from "../gpt/Ad";
import { Targeting } from "../interfaces/TargetingProvider";
import { ContentTargetingProvider, ContentTargetingType } from "./ContentTargetingProvider";
import { UserContentTargetingProvider, UserContentTargetingType } from "./UserContentTargetingProvider";
import { UserGlobalTargetingProvider, UserGlobalTargetingType } from "./UserGlobalTargetingProvider";

const ARTICLE_TARGETING_TIMEOUT = 400;
const DEFAULT_CONTENT_TARGETING_TIMEOUT = 5000;

export default class AdsServiceClient {

    private _useCachedTargeting: boolean;
    private _userInfo: UserInfo;
    private _pageId: string;
    // adContextData contains some extra page meta data like page type
    // and slug, which will be used to determine the content targetings
    // we are fetching from AdService
    private _adContextData: AdContextData = {};
    private _contentTargetingProvider;
    private _contentTargetingTimeout = DEFAULT_CONTENT_TARGETING_TIMEOUT;
    private _isDecoupled: boolean;
    private _userContentTargetingProvider: UserContentTargetingProvider;
    private _userGlobalTargetingProvider: UserGlobalTargetingProvider;
    private _excludeTargetingTypes: ExcludeTargetingType[] | undefined;
    private _disableTrigr: boolean;

    static _firstAd: {
        setFirstAd: (value: Ad) => void;
        waitForAd: Promise<Ad>;
        ad?: Ad;
    };

    constructor(userInfo: UserInfo, pageId: string, isDecoupled: boolean, useCachedTargeting = true,
        adContextData: AdContextData = {}, excludeTargetingTypes:ExcludeTargetingType[] = [],
        disableTrigr: boolean) {
        this._userInfo = userInfo;
        this._useCachedTargeting = useCachedTargeting;
        this._isDecoupled = isDecoupled;
        this._pageId = pageId;
        this._adContextData = adContextData;
        this._excludeTargetingTypes = excludeTargetingTypes;
        this._disableTrigr = disableTrigr;
        this._contentTargetingTimeout = isArticleDecoupled(
            this._adContextData,
            this._isDecoupled
        )
            ? ARTICLE_TARGETING_TIMEOUT
            : DEFAULT_CONTENT_TARGETING_TIMEOUT;
        this._contentTargetingProvider = new ContentTargetingProvider(
            adContextData,
            this._getContentTargetingTypes(),
            this._contentTargetingTimeout
        );
        this._userContentTargetingProvider = new UserContentTargetingProvider(
            adContextData,
            this._getUserContentTargetingTypes(),
            userInfo,
            this._contentTargetingTimeout
        );

        this._userGlobalTargetingProvider = new UserGlobalTargetingProvider(
            this._getUserGlobalTargetingTypes(),
            userInfo,
            this._contentTargetingTimeout
        );

        AdsServiceClient.initFirstAdHandler();
    }

    static initFirstAdHandler() {
        let resolver: (ad: Ad) => void;

        const promise = new Promise<Ad>((resolve) => {
            resolver = resolve;
        });

        this._firstAd = {
            waitForAd: promise,
            setFirstAd: resolver!
        };
    }

    static waitForFirstAd(): Promise<Ad> {
        return AdsServiceClient._firstAd.waitForAd;
    }

    setAd(ad: Ad) {
        if (!AdsServiceClient._firstAd.ad) {
            AdsServiceClient._firstAd.setFirstAd(ad);
            AdsServiceClient._firstAd.ad = ad;
        }
    }

    private _getUserContentTargetingTypes() {
        return isPersonalizedTargetingEnabled(this._adContextData, this._isDecoupled)
            ? [UserContentTargetingType.PERSONALIZATION]
            : [];
    }

    private _getUserGlobalTargetingTypes() {
        return [
            UserGlobalTargetingType.PERSONALIZATION,
            isAdserviceDVTargetingEnabled(this._pageId) ? UserGlobalTargetingType.DV : undefined,
            this._disableTrigr ? undefined : UserGlobalTargetingType.TRIGR,
            TARGETING_TYPES.IVT
        ].filter((t) => Boolean(t)) as UserGlobalTargetingType[];
    }

    private _getContentTargetingTypes() {
        return [
            isAdserviceDVTargetingEnabled(this._pageId) ? TARGETING_TYPES.DV_BRANDSAFETY : undefined,
            TARGETING_TYPES.CONTENT
        ].filter((t) => Boolean(t)) as ContentTargetingType[];
    }

    private _getTargetingUtil(promises: Promise<Targeting>[]) {
        return Promise.allSettled(promises)
            .then((results) => results.reduce((result, obj) => Object.assign(result, obj.status === "rejected" ? {} : obj.value), {}));

    }

    getTargetingFromAdsService() {
        return this._getTargetingUtil([
            this.getTargetingFromAdsServiceSync(),
            this.getTargetingFromAdsServiceAsync()
        ]);
    }

    getTargetingFromAdsServiceSync() {
        return this._excludeTargetingTypes?.includes(TARGETING_TYPES.CONTENT) ? this._getTargetingUtil([
            this._userGlobalTargetingProvider.getTargeting()
        ]) : this._getTargetingUtil([
            this._contentTargetingProvider.getTargeting(),
            this._userGlobalTargetingProvider.getTargeting()
        ]);
    }

    // use this function to get all the targetings which won't block ad loading
    getTargetingFromAdsServiceAsync() {
        return this._getTargetingUtil([
            this._userContentTargetingProvider.getTargeting()
        ]);
    }

    getTargetingsForAdSync(): Promise<Targeting> {
        return this.getTargetingFromAdsServiceSync();
    }

    getTargetingsForAdAsync(): Promise<Targeting> {
        return this.getTargetingFromAdsServiceAsync();
    }

    resetTargetings(newAdContextData: AdContextData): void {
        // if it is the same slug, no need to reset
        if (newAdContextData.slug === this._adContextData.slug) {
            return;
        }

        // If the reset call is coming from article page, it means the page is
        // transporting to a different article. In this case, we clean up the
        // _excludeTargetingTypes to fetch all types of targeting from front end
        if (newAdContextData.adPageType === "article") {
            this._excludeTargetingTypes = [];
        }

        this._adContextData = { ...this._adContextData, ...newAdContextData };
        this._contentTargetingProvider = new ContentTargetingProvider(
            newAdContextData,
            this._getContentTargetingTypes(),
            this._contentTargetingTimeout
        );
        this._userContentTargetingProvider = new UserContentTargetingProvider(
            newAdContextData,
            this._getUserContentTargetingTypes(),
            this._userInfo,
            this._contentTargetingTimeout
        );
        this.getTargetingFromAdsService();
    }

}
